import { labelIn } from "./installation-model";
import type { VocabularyOut } from "./installation-types";

/** The nouns of the project area in the viewer's language. */
export interface InstallationWords {
  /** "Project" / "Set-up": one project. */
  project: string;
  /** "Projects" / "Set-ups": several. Without a plural in the vocabulary, the neutral "Projects". */
  projects: string;
  /** "Stage" / "Phase": one stage of a project. */
  stage: string;
  /** "Stages" / "Phases". Without a plural in the vocabulary, the neutral "Stages". */
  stages: string;
  /** The word for the list of things to put right ("Open items" unless the workspace says otherwise). */
  openItems: string;
  /** The name of the area (sidebar, page title, back link): the project plural, else the neutral "Installations". */
  area: string;
}

/**
 * The workspace's words: its own settings, else its pack's, else the neutral ones (`defaults`, the screens' own wording today). A plural
 * the vocabulary does not carry keeps the neutral plural, so a workspace with no pack reads exactly what it read before.
 */
export function resolveWords(vocabulary: VocabularyOut | undefined, language: string, defaults: InstallationWords): InstallationWords {
  if (!vocabulary) return defaults;
  const projectPlural = vocabulary.project_plural ? labelIn(vocabulary.project_plural, language) : null;
  return {
    project: labelIn(vocabulary.project, language),
    projects: projectPlural ?? defaults.projects,
    stage: labelIn(vocabulary.stage, language),
    stages: vocabulary.stage_plural ? labelIn(vocabulary.stage_plural, language) : defaults.stages,
    openItems: labelIn(vocabulary.open_item, language),
    area: projectPlural ?? defaults.area,
  };
}

/** The interpolation values every string of the area may use: `{{project}}` as written, `{{projectLc}}` in lower case, and so on. */
export function wordVariables(words: InstallationWords, language: string): Record<string, string> {
  const lower = (text: string): string => text.toLocaleLowerCase(language === "fil" ? "fil" : "en");
  return {
    project: words.project,
    projectLc: lower(words.project),
    projects: words.projects,
    projectsLc: lower(words.projects),
    stage: words.stage,
    stageLc: lower(words.stage),
    stages: words.stages,
    stagesLc: lower(words.stages),
    area: words.area,
    areaLc: lower(words.area),
    items: words.openItems,
    itemsLc: lower(words.openItems),
  };
}

/** The sidebar label of the area when the vocabulary names the project plural; `undefined` keeps the neutral translated label. */
export function areaLabel(vocabulary: VocabularyOut | undefined, language: string): string | undefined {
  return vocabulary?.project_plural ? labelIn(vocabulary.project_plural, language) : undefined;
}
